import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { useAuth } from '../contexts/AuthContext';
import Logger from '../utils/logger';
import Logo from '../components/Logo';
import { Button, Input } from '../components/ui';
import { SuccessMessages } from '../constants/messages';

/**
 * Un compte existe deja a l'adresse du compte Google : l'API a envoye un code a
 * 6 chiffres a cette adresse. Le saisir prouve que le compte est bien a la
 * personne, lie l'identite Google, et ouvre la session (MFA ou verification
 * d'appareil peuvent encore suivre, comme pour le mot de passe).
 *
 * Une seule fois : ensuite le bouton Google connecte directement.
 */
const GoogleLinkPage: React.FC = () => {
  // @ts-ignore -- TS2589: useTranslation type instantiation too deep
  const { t } = (useTranslation as any)('auth');
  const {
    completeGoogleLink,
    resendGoogleLinkCode,
    hasPendingGoogleLink,
    getGoogleLinkEmailHint,
    cancelGoogleLink,
    getPendingLinkProvider,
  } = useAuth();
  const providerLabel = getPendingLinkProvider() === 'entra' ? 'Microsoft Entra ID' : 'Google';
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hasPendingGoogleLink()) {
    return <Navigate to="/login" replace />;
  }

  const emailHint = getGoogleLinkEmailHint();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await completeGoogleLink(code.trim());
      if (result.needsMfa) {
        navigate('/login/mfa', { replace: true });
        return;
      }
      if (result.needsDeviceVerification) {
        navigate('/login/verify-device', { replace: true });
        return;
      }
      toast.success(SuccessMessages.LOGIN_SUCCESS);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      Logger.error('Google link confirmation failed', err);
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      // Codes renvoyes par l'API en 400/409/410/429 (jamais 401 : l'intercepteur
      // renverrait sur /login).
      if (detail === 'invalid_code') {
        setError(t('googleLink.invalidCode'));
      } else if (detail === 'expired') {
        setError(t('googleLink.expiredCode'));
      } else if (detail === 'max_attempts') {
        setError(t('googleLink.tooManyAttempts'));
      } else if (detail === 'email_changed') {
        setError(t('googleLink.emailChanged'));
      } else if (detail === 'google_already_linked') {
        setError(t('googleLink.alreadyLinked'));
      } else {
        setError(t('googleLink.verificationFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    setResending(true);
    setError(null);
    try {
      await resendGoogleLinkCode();
      toast.success(t('googleLink.resendSuccess'));
      setCode('');
    } catch (err: unknown) {
      Logger.error('Google link code resend failed', err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 429) {
        toast.error(t('googleLink.resendRateLimited'));
      } else if (status === 410) {
        setError(t('googleLink.expiredCode'));
      } else {
        toast.error(t('googleLink.resendError'));
      }
    } finally {
      setResending(false);
    }
  };

  const onCancel = () => {
    cancelGoogleLink();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo size="large" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-text-primary">
          {t('googleLink.titleWithProvider', { provider: providerLabel })}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          {emailHint
            ? t('googleLink.subtitleWithEmailProvider', { email: emailHint, provider: providerLabel })
            : t('googleLink.subtitleWithProvider', { provider: providerLabel })}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
          <form className="space-y-6" onSubmit={onSubmit}>
            <Input
              id="google-link-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              label={t('googleLink.codeLabel') as string}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              error={error ?? undefined}
              className="text-center tracking-widest text-lg font-mono"
            />

            <p className="text-xs text-text-tertiary">{t('googleLink.hint')}</p>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={submitting}
                disabled={submitting || code.length !== 6}
                className="w-full"
              >
                {t('googleLink.verifyButton')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={onResend}
                loading={resending}
                disabled={submitting || resending}
                className="w-full"
              >
                {t('googleLink.resendButton')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={onCancel}
                className="w-full"
                disabled={submitting || resending}
              >
                {t('googleLink.cancelButton')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GoogleLinkPage;
